import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  RadioGroup,
  RadioGroupItem,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@pagespeedify/ui";
import { Plan, SubscriptionStatus } from '../../types'; // ✅ ADD: SubscriptionStatus import
import { AlertTriangle } from 'lucide-react';

interface PlanChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeUserPaddlePriceId?: string;
  plans: Plan[];
  onConfirmChange: (newPlanPlaceholderId: string) => void;
  isLoading: boolean;
  billingCycle: 'monthly' | 'yearly';
  currentPlanName: string;
  subscriptionStatus?: SubscriptionStatus;
  currentPlanIsCanceling?: boolean;
  modalMode?: 'new_subscription' | 'change_plan' | 'resume_subscription';
  // ✅ ADD: New props for cancelling subscriptions
  onResumeCurrentPlan?: () => void;
  resumeLoading?: boolean;
}

// Enhanced tier hierarchy matching backend exactly
const PLAN_TIERS = {
  'basic': 1,
  'pro': 2, 
  'enterprise': 3
} as const;

// Caching maps
const tierCache = new Map<string, keyof typeof PLAN_TIERS | null>();
const cycleCache = new Map<string, 'monthly' | 'yearly'>();
const validationCache = new Map<string, ReturnType<typeof validatePlanChange>>();

const getCurrentPlanTier = (planName: string): keyof typeof PLAN_TIERS | null => {
  if (!planName) return null;
  
  // Check cache first
  if (tierCache.has(planName)) {
    return tierCache.get(planName)!;
  }
  
  const lowerName = planName.toLowerCase();
  let tier: keyof typeof PLAN_TIERS | null = null;
  
  if (lowerName.includes('enterprise')) tier = 'enterprise';
  else if (lowerName.includes('pro')) tier = 'pro';
  else if (lowerName.includes('basic')) tier = 'basic';
  
  // Cache the result
  tierCache.set(planName, tier);
  return tier;
};

const getBillingCycleFromPlan = (planName: string): 'monthly' | 'yearly' => {
  if (!planName) return 'monthly';
  
  // Check cache first
  if (cycleCache.has(planName)) {
    return cycleCache.get(planName)!;
  }
  
  const lowerName = planName.toLowerCase();
  const isYearly = lowerName.includes('yearly') || lowerName.includes('annual') || lowerName.includes('year');
  const cycle = isYearly ? 'yearly' : 'monthly';
  
  // Cache the result
  cycleCache.set(planName, cycle);
  return cycle;
};

// ✅ OPTIMIZED: Memoized validation with cache
const validatePlanChange = (
  currentPlanName: string,
  targetPlan: Plan,
  subscriptionStatus?: string,
  currentPlanIsCanceling?: boolean // Added this parameter
): {
  allowed: boolean;
  changeType: 'upgrade' | 'downgrade' | 'same_tier' | 'reactivation' | 'disallowed_change'; // Added 'disallowed_change'
  timing: 'immediate' | 'period_end' | 'blocked';
  reason?: string;
  explanation?: string;
  warningClass?: string;
} => {
  // Create cache key
  const cacheKey = `${currentPlanName}-${targetPlan.name}-${subscriptionStatus}-${currentPlanIsCanceling}`; // Include currentPlanIsCanceling in cache key
  
  // Check cache first
  if (validationCache.has(cacheKey)) {
    return validationCache.get(cacheKey)!;
  }

  const isStrictUpgradeMode = subscriptionStatus === 'active' && !currentPlanIsCanceling;

  const currentTier = getCurrentPlanTier(currentPlanName);
  const targetTier = getCurrentPlanTier(targetPlan.name);
  const currentCycle = getBillingCycleFromPlan(currentPlanName);
  const targetCycle = getBillingCycleFromPlan(targetPlan.name);
  
  let result: ReturnType<typeof validatePlanChange>;

  if (isStrictUpgradeMode) {
    // Apply "upgrades only" logic
    if (!currentTier || !targetTier) {
      result = {
        allowed: false,
        changeType: 'disallowed_change',
        timing: 'blocked',
        reason: 'Unable to determine plan tier for upgrade comparison.',
        explanation: `❌ Cannot determine if this is an upgrade from ${currentPlanName}.`,
        warningClass: 'text-red-600 dark:text-red-400'
      };
    } else {
      // const currentCycle = getBillingCycleFromPlan(currentPlanName); // Already defined above
      // const targetCycle = getBillingCycleFromPlan(targetPlan.name);   // Already defined above
      const currentTierValue = PLAN_TIERS[currentTier];
      const targetTierValue = PLAN_TIERS[targetTier];

      let isActualUpgrade = false;
      if (targetTierValue > currentTierValue) {
        isActualUpgrade = true;
      } else if (targetTierValue === currentTierValue && currentCycle === 'monthly' && targetCycle === 'yearly') {
        isActualUpgrade = true;
      }

      if (isActualUpgrade) {
        result = {
          allowed: true,
          changeType: 'upgrade',
          timing: 'immediate',
          explanation: `⚡ Upgrade to ${targetPlan.name} immediately and get instant access to new features`,
          warningClass: 'text-green-600 dark:text-green-400'
        };
      } else {
        result = {
          allowed: false,
          changeType: 'disallowed_change', 
          timing: 'blocked',
          reason: 'Only upgrades are available for your current active plan.',
          explanation: `❌ Only upgrades are available from ${currentPlanName}.`,
          warningClass: 'text-red-600 dark:text-red-400'
        };
      }
    }
  } else {
    // Original permissive logic for other scenarios (canceled, or active but canceling)
    if (!currentTier || !targetTier) {
      result = { 
        allowed: false, 
        changeType: 'downgrade', 
        timing: 'blocked',
        reason: 'Unable to determine plan tier',
        explanation: 'This plan change is not available',
        warningClass: 'text-red-600 dark:text-red-400'
      };
    } else {
      const isDowngrade = PLAN_TIERS[targetTier] < PLAN_TIERS[currentTier];
      const isUpgrade = PLAN_TIERS[targetTier] > PLAN_TIERS[currentTier];
      const isSameTier = PLAN_TIERS[targetTier] === PLAN_TIERS[currentTier];
      const isBillingCycleChange = currentCycle !== targetCycle;

      if (isSameTier && !isBillingCycleChange) { // Reactivation logic
        result = {
          allowed: true,
          changeType: 'reactivation',
          timing: 'immediate',
          explanation: 'This is your current plan',
          warningClass: 'text-gray-600 dark:text-gray-400'
        };
      } else { // Not reactivation, so it's a potential plan change
        if (isDowngrade && isBillingCycleChange) { // Blocked: Cross-tier downgrade with billing cycle change
          result = {
            allowed: false,
            changeType: 'downgrade',
            timing: 'blocked',
            reason: `Cross-tier downgrade with billing cycle change not supported`,
            explanation: `❌ Cannot switch from ${currentPlanName} to ${targetPlan.name}. Try a ${currentCycle} plan instead.`,
            warningClass: 'text-red-600 dark:text-red-400'
          };
        } else {
          if (isUpgrade) { // Allowed: All tier upgrades
            result = {
              allowed: true,
              changeType: 'upgrade',
              timing: 'immediate',
              explanation: `⚡ Upgrade to ${targetPlan.name} immediately and get instant access to new features`,
              warningClass: 'text-green-600 dark:text-green-400'
            };
          } else {
            if (isDowngrade && !isBillingCycleChange) { // Allowed: Tier downgrade, same billing cycle
              result = {
                allowed: true,
                changeType: 'downgrade',
                timing: 'period_end',
                explanation: `⏰ ${targetPlan.name} will activate at the end of your current billing period`,
                warningClass: 'text-orange-600 dark:text-orange-400'
              };
            } else {
              if (isSameTier && isBillingCycleChange) { // Allowed: Same tier, billing cycle change
                const isYearlyToMonthly = currentCycle === 'yearly' && targetCycle === 'monthly';
                if (isYearlyToMonthly) {
                  result = {
                    allowed: true,
                    changeType: 'same_tier',
                    timing: 'period_end',
                    explanation: `⏰ Switch to ${targetPlan.name} at the end of your current billing period`,
                    warningClass: 'text-orange-600 dark:text-orange-400'
                  };
                } else { // Monthly to Yearly
                  result = {
                    allowed: true,
                    changeType: 'same_tier',
                    timing: 'immediate',
                    explanation: `⚡ Switch to ${targetPlan.name} now and start saving immediately`,
                    warningClass: 'text-blue-600 dark:text-blue-400'
                  };
                }
              } else { // Fallback: Should not be reached if logic is exhaustive for permissive changes
                result = {
                  allowed: false,
                  changeType: 'disallowed_change',
                  timing: 'blocked',
                  reason: 'This specific plan combination is not available.',
                  explanation: '❌ This plan change is not currently available.',
                  warningClass: 'text-gray-600 dark:text-gray-400'
                };
              }
            }
          }
        }
      }
    }
  } // This is the closing brace for the main `else` (permissive logic) that follows `if (isStrictUpgradeMode)`
  // The extra brace that was on line 251 has been removed.
  // Cache the result
  validationCache.set(cacheKey, result);
  return result;
}; // This is the single, correct closing brace for the `validatePlanChange` function.

// ✅ FIX 1: Remove the problematic useCallback - make it a simple function
const validatePlanForCancelling = (targetPlan: Plan) => ({
  allowed: true,
  changeType: 'new_subscription' as const,
  timing: 'immediate' as const,
  explanation: `Switch to ${targetPlan.name} and resume subscription`,
  warningClass: 'text-blue-600 dark:text-blue-400'
});

// Enhanced blocked plans info component - USER-FOCUSED
const BlockedPlansInfo = ({ 
  currentPlanName, 
  blockedCount 
}: { 
  currentPlanName: string; 
  blockedCount: number;
}): JSX.Element | null => {
  const currentCycle = getBillingCycleFromPlan(currentPlanName);
  const currentTier = getCurrentPlanTier(currentPlanName);
  
  if (!currentTier || blockedCount === 0) return null;
  
  return (
    <div className="p-3 bg-muted/50 rounded-md border">
      <p className="text-xs text-muted-foreground text-left">
        {blockedCount} plan{blockedCount > 1 ? 's are' : ' is'} not available from your current {currentPlanName} plan.
      </p>
    </div>
  );
};

// Enhanced plan option component - IMPROVED VERSION with better labels and icons
const PlanOption = ({ 
  option, 
  type, 
  currentPlanName, 
  subscriptionStatus,
  validation,
  isBlocked = false
}: { 
  option: any; 
  type: 'monthly' | 'yearly';
  currentPlanName: string;
  subscriptionStatus?: string;
  validation: ReturnType<typeof validatePlanChange>;
  isBlocked?: boolean;
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // ✅ For blocked plans - COMPACT VERSION
  if (isBlocked) {
    return (
      <div className="flex items-center space-x-2 border p-2 rounded opacity-60 cursor-not-allowed border-gray-300 dark:border-gray-600"> {/* ✅ SMALLER: p-2 */}
        <div className="w-4 h-4 flex-shrink-0" />
        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="font-medium text-gray-500 text-sm">{option.name}</span> {/* ✅ SMALLER: text-sm */}
              <span className="text-xs text-gray-500">{option.priceFormatted}</span> {/* ✅ SMALLER: text-xs */}
            </div>
            <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded"> {/* ✅ SMALLER: px-1.5 py-0.5 */}
              Not Available
            </span>
          </div>
          
          {validation.explanation && (
            <span className={`text-xs mt-1 text-left ${validation.warningClass || 'text-gray-600 dark:text-gray-400'}`}>
              {validation.explanation}
            </span>
          )}
        </div>
      </div>
    );
  }

  // ✅ For available plans - COMPACT VERSION with expandable details
  return (
    <div className="border rounded transition-colors hover:border-primary dark:hover:border-primary-foreground">
      {/* MAIN CONTENT - Always visible */}
      <div className="flex items-center space-x-2 p-2 cursor-pointer"> 
        <RadioGroupItem 
          value={option.paddlePriceId} 
          id={`plan-${type}-${option.paddlePriceId}`}
          className="radio-ring" // ✅ ADDED CLASS HERE
        />
        <Label 
          htmlFor={`plan-${type}-${option.paddlePriceId}`} 
          className="flex flex-col w-full cursor-pointer text-left" // Ensure parent Label forces text-left for children
        >
          {/* MODIFIED SECTION: This div wraps the plan name/price and badges */}
          <div className="flex justify-between items-start w-full"> {/* USE THIS: items-start for top alignment, w-full for justify-between */}
            {/* Left Column: Plan Name and Price Details */}
            <div className="flex flex-col"> {/* This part remains structurally similar, inherits text-left */}
              <span className="font-medium text-sm">{option.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{option.priceFormatted}</span>
                {option.effectiveMonthlyPrice && (
                  <span className="text-xs text-muted-foreground">
                    ({option.effectiveMonthlyPrice})
                  </span>
                )}
              </div>
            </div>
            
            {/* Right Column: Badges (stacked and aligned to the right) */}
            <div className="flex flex-col items-end gap-1 ml-2"> {/* USE THIS: flex-col to stack, items-end to align right, gap-1 for spacing between badges, ml-2 for space from left content */}
              {/* Yearly discount badge (ensure this is first if both apply) */}
              {option.yearlyDiscount && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-center">
                  {option.yearlyDiscount}
                </span>
              )}
              
              {/* ADDED: Instant Resume badge for cancelling/new_subscription mode */}
              {validation.changeType === 'new_subscription' && validation.timing === 'immediate' && (
                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded whitespace-nowrap">
                  Instant Resume
                </span>
              )}
              
              {/* Existing Status indicators */}
              {validation.changeType === 'upgrade' && (
                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded whitespace-nowrap">
                  Instant Upgrade
                </span>
              )}
              {validation.changeType === 'downgrade' && (
                <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded whitespace-nowrap">
                  Downgrade at Period End
                </span>
              )}
              {validation.changeType === 'same_tier' && validation.timing === 'immediate' && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded whitespace-nowrap">
                  Instant Switch
                </span>
              )}
              {validation.changeType === 'same_tier' && validation.timing === 'period_end' && (
                <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 rounded whitespace-nowrap">
                  Switch at Period End
                </span>
              )}
            </div>
          </div>
          {/* END OF MODIFIED SECTION */}
          
          {/* Validation explanation - this should already be correctly placed and left-aligned */}
          {validation.explanation && (
            <span className={`block w-full text-xs mt-1 text-left ${validation.warningClass || 'text-gray-600 dark:text-gray-400'}`}> {/* ✅ ADDED 'w-full' */}
              {validation.explanation}
            </span>
          )}
        </Label>
        
        {/* ✅ IMPROVED: Better expand/collapse icon */}
        {(option.description || (option.features && Object.keys(option.features).length > 0)) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="p-1 h-5 w-5 flex-shrink-0" // ✅ SMALLER: h-5 w-5
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowDetails(!showDetails);
            }}
          >
            {/* ✅ IMPROVED: Use chevron icons instead of +/- */}
            {showDetails ? (
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </Button>
        )}
      </div>
      
      {/* ✅ EXPANDABLE DETAILS - Only shown when toggled */}
      {showDetails && (
        <div className="border-t px-2 pb-2 pt-1  space-y-1.5"> {/* ✅ SMALLER: px-2 pb-2 pt-1 space-y-1.5 */}
          {/* Description */}
          {option.description && (
            <div>
              <p className="text-xs font-medium text-foreground mb-0.5">Description:</p> {/* ✅ SMALLER: mb-0.5 */}
              <p className="text-xs text-muted-foreground leading-relaxed">
                {option.description}
              </p>
            </div>
          )}
          
          {/* Features - UPDATED: Use PlanFeaturesDisplay component */}
          <PlanFeaturesDisplay 
            features={option.features} 
            showActivationPerks={true}
            showMonthlyEquivalent={type === 'yearly'}
            className="mt-1"
          />
        </div>
      )}
    </div>
  );
};

// --- START OF MODIFICATIONS for PlanFeaturesDisplay and its helpers ---

// Define which feature keys from the 'features' object are currently live and should be displayed.
const LIVE_FEATURE_KEYS: string[] = [
  'reports',
  'agent_compute_units',
  'image_optimizations',
  'competitor_reports',
  // 'monitoring_metrics', // This is a future feature, so it's commented out/excluded.
];

// Helper for user-friendly feature labels
const planFeatureDisplayLabels: Record<string, string> = {
  reports: 'PageSpeed Reports',
  agent_compute_units: 'Compute Units',
  image_optimizations: 'Image Optimizations',
  competitor_reports: 'Competitor Analysis Reports',
  monitoring_metrics: 'Performance Monitoring Checks',
};

// SVG Checkmark Icon component
const CheckIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
  </svg>
);

// Updated PlanFeaturesDisplay
const PlanFeaturesDisplay: React.FC<{
  features: any;
  showActivationPerks?: boolean;
  className?: string;
  showMonthlyEquivalent?: boolean;
}> = ({ features, className = "", showMonthlyEquivalent = false }) => {
  if (!features || typeof features !== 'object') {
    return <p className={`text-xs text-muted-foreground ${className}`}>Feature details not available.</p>;
  }

  const {
    term_included_allocations: termAllocations,
    billing_term: billingTerm,
    overage_costs_credits: overageCostsCredits,
  } = features;

  const getBillingPeriodText = (term?: string) => {
    if (term === 'monthly') return 'per month';
    if (term === 'yearly') return 'per year';
    return '';
  };

  const liveTermAllocations = termAllocations && typeof termAllocations === 'object'
    ? Object.entries(termAllocations).filter(([key]) => LIVE_FEATURE_KEYS.includes(key))
    : [];

  const liveOverageCosts = overageCostsCredits && typeof overageCostsCredits === 'object'
    ? Object.entries(overageCostsCredits).filter(([key]) => LIVE_FEATURE_KEYS.includes(key))
    : [];

  return (
    <div className={`space-y-3 text-xs ${className}`}>
      {/* Included Allocations */}
      {liveTermAllocations.length > 0 && (
        <div>
          <h4 className="font-medium text-foreground mb-1.5">
            Included with this plan:
          </h4>
          <ul className="space-y-1">
            {liveTermAllocations.map(([key, value]) => {
              const label = planFeatureDisplayLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              const periodText = getBillingPeriodText(billingTerm);
              const monthlyEquiv = showMonthlyEquivalent && billingTerm === 'yearly' && typeof value === 'number' && value > 0
                ? ` (~${Math.round(Number(value) / 12)}/month)`
                : '';
              return (
                <li key={key} className="flex items-start text-muted-foreground">
                  <CheckIcon className="w-3.5 h-3.5 mr-1.5 mt-0.5 text-green-600 dark:text-green-500 flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">{String(value)}</strong> {label} {periodText}
                    {monthlyEquiv && <span className="text-xs text-muted-foreground ml-1">{monthlyEquiv}</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}


      
      {/* Unlimited Access With Credits (formerly Overage Costs) */}
      {liveOverageCosts.length > 0 && (
         <div className="pt-2 mt-3 border-t border-border">
          {/* MODIFICATION: Removed CheckIcon from heading */}
          <h4 className="font-medium text-foreground mb-1.5">
            Unlimited Access With Credits:
          </h4>
          <ul className="space-y-1 pl-1"> {/* MODIFICATION: Adjusted padding if needed, or keep pl-5 if preferred for sub-items */}
            {liveOverageCosts.map(([key, value]) => {
              const label = planFeatureDisplayLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <li key={key} className="flex items-start text-muted-foreground">
                   {/* MODIFICATION: Added CheckIcon to list items */}
                  <CheckIcon className="w-3.5 h-3.5 mr-1.5 mt-0.5 text-green-600 dark:text-green-500 flex-shrink-0" />
                  <span>
                    {label}: <strong className="text-foreground">{String(value)} credits</strong> per unit
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Informational Note about Credits - REMOVED FROM HERE */}
      {/* It will be moved to the main modal component */}
    </div>
  );
};

// --- END OF MODIFICATIONS for PlanFeaturesDisplay and its helpers ---

export function PlanChangeModal({
  isOpen,
  onClose,
  activeUserPaddlePriceId,
  plans,
  onConfirmChange,
  isLoading,
  billingCycle,
  modalMode = 'change_plan',
  currentPlanName,
  subscriptionStatus,
  currentPlanIsCanceling,
}: PlanChangeModalProps) {
  const [selectedPaddlePriceId, setSelectedPaddlePriceId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'monthly' | 'yearly'>(billingCycle || 'monthly');

  const availableOfferings = useMemo(() => {
    if (currentPlanIsCanceling || modalMode === 'new_subscription') {
      // Mode: Changing from a CANCELLING plan or choosing a NEW subscription.
      // Filter out the plan that is currently cancelling (or was last active if new_subscription)
      // as "Resume" or re-selecting it from a list handles that.
      // Also filter out free plans and plans without valid Paddle IDs.
      return plans.filter(plan => {
        const isThePlanToResumeViaDedicatedButton = plan.name === currentPlanName;
        const isFreePlan = plan.name?.toLowerCase() === 'free';
        const hasValidPaddleId = typeof plan.paddle_price_id === 'string' && plan.paddle_price_id.startsWith('pri_');
        
        return !isThePlanToResumeViaDedicatedButton && !isFreePlan && hasValidPaddleId;
      });
    } else {
      // Mode: Standard "Change Plan" from an ACTIVE, NON-CANCELING subscription.
      // Filter out the current active plan itself and free plans.
      return plans.filter(plan => {
        const isCurrentActivePlan = plan.paddle_price_id === activeUserPaddlePriceId;
        const isFreePlan = plan.name?.toLowerCase() === 'free';
        const hasValidPaddleId = typeof plan.paddle_price_id === 'string' && plan.paddle_price_id.startsWith('pri_');
        
        return !isCurrentActivePlan && !isFreePlan && hasValidPaddleId;
      });
    }
  }, [plans, activeUserPaddlePriceId, currentPlanIsCanceling, modalMode, currentPlanName]); // Added currentPlanName and modalMode

  const monthlyOptions = useMemo(() => {
    const monthlyPlanFilter = (plan: Plan) => {
      return plan.price_monthly != null && 
             Number(plan.price_monthly) > 0 && 
             (plan.price_yearly == null || Number(plan.price_yearly) === 0);
    };

    // Now works with pre-filtered availableOfferings
    return availableOfferings
      .filter(monthlyPlanFilter)
      .map(plan => ({
        ...plan,
        planTypeId: plan.id,
        paddlePriceId: plan.paddle_price_id!,
        name: plan.name, 
        description: plan.description,
        priceFormatted: `$${Number(plan.price_monthly).toFixed(2)}/month`,
      }));
  }, [availableOfferings]); // Dependency is now just availableOfferings

  // ✅ OPTIMIZED: Memoized yearly options
  const yearlyOptions = useMemo(() => {
    console.log('Yearly Options Calculation - Input `plans` prop to modal:', JSON.stringify(plans, null, 2));
    console.log('Yearly Options Calculation - `availableOfferings` (pre-filtered):', JSON.stringify(availableOfferings, null, 2));


    const yearlyPlanFilter = (plan: Plan) => {
      return plan.price_yearly != null && 
             Number(plan.price_yearly) > 0 && 
             (plan.price_monthly == null || Number(plan.price_monthly) === 0);
    };

    // Now works with pre-filtered availableOfferings
    return availableOfferings
      .filter(yearlyPlanFilter)
      .map(yearlyPlan => {
        const baseName = yearlyPlan.name.replace(' Yearly', '');
        // IMPORTANT: For savings calculation, `plans` (the original full list) is still needed
        // to find the corresponding monthly plan, as `availableOfferings` might have filtered it out.
        const correspondingMonthlyPlan = plans.find(p => p.name === `${baseName} Monthly`);
        let yearlyDiscount: string | null = null;

        // DEBUGGING FOR "Pro Yearly"
        if (yearlyPlan.name === 'Pro Yearly') {
          console.log('Debugging Pro Yearly Savings (within yearlyOptions):');
          console.log('  Yearly Plan from availableOfferings:', yearlyPlan);
          console.log('  Base Name:', baseName);
          console.log('  Corresponding Monthly Plan Found (from original `plans`):', correspondingMonthlyPlan);
        }

        if (correspondingMonthlyPlan && correspondingMonthlyPlan.price_monthly) {
          const monthlyPrice = Number(correspondingMonthlyPlan.price_monthly);
          const yearlyPrice = Number(yearlyPlan.price_yearly);
          const monthlyTotal = monthlyPrice * 12;

          if (yearlyPlan.name === 'Pro Yearly') {
            console.log('  Monthly Price (from corresponding plan):', monthlyPrice);
            console.log('  Yearly Price (from Pro Yearly):', yearlyPrice);
            console.log('  Calculated Monthly Total (x12):', monthlyTotal);
            console.log('  Is Monthly Total > Yearly Price?', monthlyTotal > yearlyPrice);
          }

          if (monthlyTotal > yearlyPrice) {
            const savings = ((monthlyTotal - yearlyPrice) / monthlyTotal) * 100;
            yearlyDiscount = `Save ${Math.round(savings)}% vs Monthly`;
            if (yearlyPlan.name === 'Pro Yearly') {
              console.log('  Calculated Savings:', savings, '%');
              console.log('  Resulting yearlyDiscount string:', yearlyDiscount);
            }
          } else if (yearlyPlan.name === 'Pro Yearly') {
            console.log('  No savings: monthlyTotal is not greater than yearlyPrice.');
          }
        } else if (yearlyPlan.name === 'Pro Yearly') {
          console.log('  No corresponding monthly plan or monthly price found for Pro Yearly in original `plans`.');
        }

        return {
          ...yearlyPlan,
          planTypeId: yearlyPlan.id,
          paddlePriceId: yearlyPlan.paddle_price_id!,
          name: yearlyPlan.name,
          description: yearlyPlan.description || correspondingMonthlyPlan?.description,
          features: yearlyPlan.features || correspondingMonthlyPlan?.features,
          priceFormatted: `$${Number(yearlyPlan.price_yearly).toFixed(2)}/year`,
          effectiveMonthlyPrice: correspondingMonthlyPlan?.price_monthly ? `$${(Number(yearlyPlan.price_yearly) / 12).toFixed(2)}/month` : null,
          yearlyDiscount,
        };
      });
  }, [availableOfferings, plans]); // Dependency includes availableOfferings and original `plans` (for savings calc)

  // ✅ OPTIMIZED: Memoized validation results
  const monthlyValidations = useMemo(() => {
    return monthlyOptions.map(option => ({
      ...option,
      validation: (currentPlanIsCanceling || modalMode === 'new_subscription') 
        ? validatePlanForCancelling(option)
        : validatePlanChange(currentPlanName, option, subscriptionStatus, currentPlanIsCanceling)
    }));
  }, [monthlyOptions, currentPlanName, subscriptionStatus, currentPlanIsCanceling, modalMode, validatePlanForCancelling]);

  const yearlyValidations = useMemo(() => {
    return yearlyOptions.map(option => ({
      ...option,
      validation: (currentPlanIsCanceling || modalMode === 'new_subscription') 
        ? validatePlanForCancelling(option)
        : validatePlanChange(currentPlanName, option, subscriptionStatus, currentPlanIsCanceling)
    }));
  }, [yearlyOptions, currentPlanName, subscriptionStatus, currentPlanIsCanceling, modalMode, validatePlanForCancelling]);

  // ✅ OPTIMIZED: Memoized blocked count calculation
  const totalBlockedCount = useMemo(() => {
    return [...monthlyValidations, ...yearlyValidations]
      .filter(option => !option.validation.allowed)
      .length;
  }, [monthlyValidations, yearlyValidations]);

  // ✅ OPTIMIZED: Callback to prevent re-renders
  const handleConfirm = useCallback(() => {
    if (selectedPaddlePriceId) {
      onConfirmChange(selectedPaddlePriceId);
    }
  }, [selectedPaddlePriceId, onConfirmChange]);

  // ✅ OPTIMIZED: Callback for tab change
  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as 'monthly' | 'yearly');
  }, []);

  // ✅ OPTIMIZED: Memoized render function
  const renderPlanOptions = useCallback((
    validatedOptions: (typeof monthlyValidations | typeof yearlyValidations), 
    type: 'monthly' | 'yearly'
  ) => {
    // ✅ SIMPLE: Just this one condition for cancelling subscriptions
    if (currentPlanIsCanceling) {
      const availableOptions = validatedOptions.filter(option => option.validation.allowed);
      
      return (
        <div className="space-y-2">
          <RadioGroup 
            value={selectedPaddlePriceId ?? undefined} 
            onValueChange={setSelectedPaddlePriceId} 
            className="space-y-1.5"
          >
            {availableOptions.map((option) => (
              <PlanOption
                key={`${type}-${option.paddlePriceId}`}
                option={option}
                type={type}
                currentPlanName={currentPlanName}
                subscriptionStatus={subscriptionStatus}
                validation={option.validation}
                isBlocked={false}
              />
            ))}
          </RadioGroup>
        </div>
      );
    }

    // ✅ EXISTING: Your current logic for active subscriptions - don't change anything else
    const availableOptionsForTab = validatedOptions.filter(option => option.validation.allowed);
    const blockedOptionsForTab = validatedOptions.filter(option => !option.validation.allowed);

    if (validatedOptions.length === 0) {
      return <p className="text-xs text-center text-muted-foreground py-4">No {type} plans available.</p>;
    }

    return (
      <div className="space-y-2">
        {availableOptionsForTab.length > 0 && (
          <div>
            {/* Section headings - left-aligned */}
            <h4 className="text-xs font-medium text-foreground mb-2 text-left">
              Available Plans ({availableOptionsForTab.length})
            </h4>
            <RadioGroup 
              value={selectedPaddlePriceId ?? undefined} 
              onValueChange={setSelectedPaddlePriceId} 
              className="space-y-1.5"
            >
              {availableOptionsForTab.map((option) => (
                <PlanOption
                  key={`${type}-${option.paddlePriceId}`}
                  option={option}
                  type={type}
                  currentPlanName={currentPlanName}
                  subscriptionStatus={subscriptionStatus}
                  validation={option.validation}
                  isBlocked={false}
                />
              ))}
            </RadioGroup>
          </div>
        )}

        {/* ✅ CONDITIONAL: Only show blocked plans for active subscriptions */}
        {blockedOptionsForTab.length > 0 && (
          <div className="space-y-1.5">
            <h4 className="text-xs font-medium text-muted-foreground border-t pt-2">
              Unavailable Plans ({blockedOptionsForTab.length})
            </h4>
            <div className="space-y-1.5">
              {blockedOptionsForTab.map((option) => (
                <PlanOption
                  key={`blocked-${type}-${option.paddlePriceId}`}
                  option={option}
                  type={type}
                  currentPlanName={currentPlanName}
                  subscriptionStatus={subscriptionStatus}
                  validation={option.validation}
                  isBlocked={true}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [selectedPaddlePriceId, currentPlanName, subscriptionStatus, currentPlanIsCanceling, modalMode]);

  // ✅ OPTIMIZED: Minimal debug logging only when modal opens
  useEffect(() => {
    if (isOpen && process.env.NODE_ENV === 'development') {
      console.log('🔍 PlanChangeModal opened - Debug summary:');
      console.log('Current plan:', currentPlanName);
      console.log('Available monthly options:', monthlyOptions.length);
      console.log('Available yearly options:', yearlyOptions.length);
      console.log('Total blocked plans:', totalBlockedCount);
    }
  }, [isOpen, currentPlanName, monthlyOptions, yearlyOptions, totalBlockedCount]); // Added dependencies for accurate logging

  useEffect(() => {
    if (isOpen) {
      setSelectedPaddlePriceId(null); 
      setActiveTab(billingCycle || 'monthly'); 
    }
  }, [isOpen, billingCycle]);

  // ✅ OPTIMIZED: Memoized current available options
  const currentAvailableOptions = useMemo(() => {
    const currentOptions = activeTab === 'monthly' ? monthlyValidations : yearlyValidations;
    return currentOptions.filter(option => option.validation.allowed);
  }, [activeTab, monthlyValidations, yearlyValidations]);

  // ✅ ADD: Cache cleanup in useEffect
  useEffect(() => {
    if (!isOpen) {
      // Clear caches when modal closes to prevent memory leaks
      tierCache.clear();
      cycleCache.clear();
      validationCache.clear();
    }
  }, [isOpen]);

  // ✅ ADD: Dynamic modal content based on mode
  const modalContent = useMemo(() => {
    if (modalMode === 'new_subscription') {
      return {
        title: 'Choose a Plan',
        description: 'Choose your new plan. All plans give you instant access.'
      };
    }
    
    if (currentPlanIsCanceling) {
      return {
        title: 'Change Plan & Resume',
        description: 'Choose a different plan to switch to and resume your subscription. If you want to keep your current plan, use the "Resume" button instead.'
      };
    }
    
    return {
      title: 'Change Plan',
      description: 'Choose your new plan. Upgrades give you instant access.'
    };
  }, [modalMode, currentPlanIsCanceling]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg">{modalContent.title}</DialogTitle>
          <DialogDescription className="text-sm">
            {modalContent.description}
          </DialogDescription>
        </DialogHeader>

        {currentPlanIsCanceling && (
          <div className="p-3 border rounded-md bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <p className="text-xs text-muted-foreground text-left">
              💡 <strong>Want to keep your current {currentPlanName} plan?</strong> Close this window and use the "Resume" button instead.
            </p>
          </div>
        )}

        {/* MODIFICATION: Removed BlockedPlansInfo component display */}
        {/* {currentPlanName && !currentPlanIsCanceling && totalBlockedCount > 0 && (
          <BlockedPlansInfo 
            currentPlanName={currentPlanName} 
            blockedCount={totalBlockedCount}
          />
        )} */}

        <div className="my-3 p-3 border rounded-md bg-muted/30 dark:bg-muted/10">
          <p className="text-xs text-muted-foreground italic text-center">
            ✨ Ran out of included allowances? No problem! You can continue using all features by purchasing credits. Your access is never truly limited.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <div className="flex justify-center mb-3">
            <TabsList className="grid w-full grid-cols-2 max-w-[200px]">
              <TabsTrigger value="monthly" className="text-sm">Monthly</TabsTrigger>
              <TabsTrigger value="yearly" className="text-sm">Yearly</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="monthly" className="space-y-2">
            {renderPlanOptions(monthlyValidations, 'monthly')}
          </TabsContent>
          <TabsContent value="yearly" className="space-y-2">
            {renderPlanOptions(yearlyValidations, 'yearly')}
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-col space-y-2 pt-3">
          <div className="flex justify-end space-x-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={!selectedPaddlePriceId || isLoading}
            >
              {isLoading ? 'Processing...' : 
               currentPlanIsCanceling ? 'Switch to This Plan & Resume' : 'Confirm Change'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
